import { useCallback, useEffect, useState } from "react";
import { KeyRound, Trash2, UserPlus, Loader2, Server } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { PillButton } from "@/components/sources/shared";
import { AddUserDialog, LibrarySelect, PasswordDialog, ROLE_LABEL } from "@/components/admin/UserDialogs";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const UserRow = ({ u, me, owners, onChange, onPassword, onDelete }) => (
  <tr className="border-b border-zinc-100 last:border-0" data-testid={`user-row-${u.email}`}>
    <td className="py-4 pr-4">
      <p className="text-sm font-medium">{u.name}{u.id === me && <span className="ml-2 font-mono text-[10px] text-zinc-400">you</span>}</p>
      <p className="font-mono text-[11px] text-zinc-500">{u.email}</p>
    </td>
    <td className="py-4 pr-4">
      <Select value={u.role} onValueChange={(role) => onChange(u, { role })} disabled={u.id === me}>
        <SelectTrigger className="h-9 w-40 bg-white text-xs" data-testid={`user-role-select-${u.email}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`user-role-option-${v}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </td>
    <td className="py-4 pr-4">
      <LibrarySelect value={u.shared_from} onChange={(shared_from) => onChange(u, { shared_from })} owners={owners.filter((o) => o.id !== u.id)} testid={`user-library-select-${u.email}`} />
    </td>
    <td className="py-4 pr-4 font-mono text-[11px] text-zinc-500" data-testid={`user-sources-${u.email}`}>
      <span className="flex items-center gap-2">
        {u.plex && <Server size={12} className="text-amber-700" />}
        {u.shared_from ? "shared" : `${u.videos} videos`}
      </span>
    </td>
    <td className="py-4 pr-4 font-mono text-[11px] text-zinc-500">{when(u.last_watched)}</td>
    <td className="py-4 text-right">
      <button onClick={() => onPassword(u)} title="Reset password" data-testid={`user-reset-password-${u.email}`} className="rounded-md p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"><KeyRound size={15} /></button>
      <button onClick={() => onDelete(u)} disabled={u.id === me} title="Delete account" data-testid={`user-delete-${u.email}`} className="rounded-md p-2 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"><Trash2 size={15} /></button>
    </td>
  </tr>
);

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const owners = (rows || []).filter((u) => !u.shared_from && u.videos > 0).map((u) => ({ id: u.id, name: u.name || u.email, videos: u.videos }));
  const change = async (u, patch) => {
    try { await api.put(`/admin/users/${u.id}`, patch); toast.success(`Updated ${u.email}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? Their watch history and any sources they connected are removed. Nothing changes on Plex.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success(`Deleted ${u.email}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Manage" title="Users" testid="users-title" sub={rows ? `${rows.length} accounts · family members can watch a shared library without their own server` : " "}>
        <PillButton onClick={() => setAdding(true)} testid="add-user-button"><UserPlus size={14} />Add family member</PillButton>
      </PageHeader>
      <div className="max-w-6xl overflow-x-auto rounded-xl border border-zinc-200 bg-white px-6 animate-rise" data-testid="users-table">
        {!rows ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-300" /></div> : (
          <table className="w-full min-w-[820px] text-left">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
                <th className="py-4 pr-4 font-semibold">Account</th><th className="py-4 pr-4 font-semibold">Role</th><th className="py-4 pr-4 font-semibold">Library</th>
                <th className="py-4 pr-4 font-semibold">Sources</th><th className="py-4 pr-4 font-semibold">Last watched</th><th />
              </tr>
            </thead>
            <tbody>{rows.map((u) => <UserRow key={u.id} u={u} me={user.id} owners={owners} onChange={change} onPassword={setPwUser} onDelete={remove} />)}</tbody>
          </table>
        )}
      </div>
      {adding && <AddUserDialog open={adding} onOpenChange={setAdding} owners={owners} defaultOwner={owners.find((o) => o.id === user.id)?.id} onDone={load} />}
      <PasswordDialog user={pwUser} onOpenChange={(o) => !o && setPwUser(null)} />
    </div>
  );
}
